import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { uiText } from '../../i18n';
import './boot-skeleton.css';

/* Скелетон первого открытия в серверном режиме, пока каркас получает сессию и каталог: формы «Озвучки» шаблона
   (SK.tts в features/shell/engine.ts — поиск с сортировкой, вкладки, строки голосов) плюс шапка и меню .dock.
   Свои стили (boot-skeleton.css): стили каркаса (legacy/shell.css, .vsk) подключаются позже, вместе с ним самим. */

/** Ширины подписей строк голосов — как у SK.tts */
const TITLES = ['34%', '26%', '30%', '40%', '28%', '36%', '32%', '38%', '30%'];
const SUBTITLES = ['52%', '44%', '48%', '58%', '40%', '54%', '46%', '50%', '42%'];
/** Вкладки «Рекомендуем · Подборки · Новые · Мои · Персонажи» */
const TABS = ['104px', '88px', '66px', '52px', '96px'];

/* Пороги — как у скелетона смены раздела шаблона (SK_GATE, SK_HALF, SK_MIN в features/shell/engine.ts): проявляется не раньше GATE мс,
   поэтому быстрый вход его не показывает вовсе; едва проявившийся (меньше HALF мс) уходит сразу, заметный стоит не меньше MIN мс и гаснет за OUT мс */
const GATE = 120;
const HALF = 80;
const MIN = 280;
const OUT = 160;

/** Блок скелетона: o — круг, p — таблетка */
function Block({ w, h, shape }: { w: string; h: number; shape?: 'o' | 'p' }) {
  return <i className={'b' + (shape ? ' ' + shape : '')} style={{ width: w, height: h }} />;
}

/** done — приложение готово: скелетон уходит без мигания и зовёт onGone, когда его можно убрать из дерева */
export function BootSkeleton({ done, onGone }: { done: boolean; onGone: () => void }) {
  const label = uiText('Загружаем голоса…');
  const t0 = useRef(performance.now());
  const [out, setOut] = useState(false);
  const gone = useRef(onGone);
  gone.current = onGone;
  useEffect(() => {
    if (!done) return;
    const shown = performance.now() - t0.current - GATE;
    if (shown <= 0 || matchMedia('(prefers-reduced-motion: reduce)').matches) return void gone.current();
    const hold = shown < HALF ? 0 : Math.max(0, MIN - shown);
    const fade = setTimeout(() => setOut(true), hold);
    const drop = setTimeout(() => gone.current(), hold + OUT);
    return () => { clearTimeout(fade); clearTimeout(drop); };
  }, [done]);
  return (
    <main className={'boot-sk' + (out ? ' out' : '')} role="status" aria-busy={!done} aria-label={label}>
      <span className="boot-sk__sr">{label}</span>
      <div className="boot-sk__in" aria-hidden="true">
        <div className="r boot-sk__head">
          <Block w="38%" h={24} />
          <Block w="36px" h={36} shape="o" />
        </div>
        <div className="r">
          <Block w="calc(100% - 50px)" h={40} shape="p" />
          <Block w="38px" h={38} shape="o" />
        </div>
        <div className="r boot-sk__tabs">
          {TABS.map((w, i) => <Block key={i} w={w} h={32} shape="p" />)}
        </div>
        {TITLES.map((w, i) => (
          <div key={i} className="r boot-sk__row" style={{ '--d': `${60 * (i + 3)}ms` } as CSSProperties}>
            <Block w="44px" h={44} shape="o" />
            <div className="c">
              <Block w={w} h={13} />
              <Block w={SUBTITLES[i]} h={10} />
            </div>
          </div>
        ))}
      </div>
      <div className="boot-sk__dock" aria-hidden="true">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className="boot-sk__tab">
            <Block w="22px" h={22} shape="o" />
            <Block w="38px" h={8} shape="p" />
          </span>
        ))}
      </div>
    </main>
  );
}
